// A ordem da tela Hoje — Rizoma_Manejo_Operacoes_v2_Spec.md §6.1.
//
// «Ordem: atrasadas (vermelho) → em andamento → planejadas de hoje por prioridade.»
//
// Mora em shared/ e não dentro do componente por um motivo prático: é a única
// regra da tela do operador que dá para testar sem DOM (o repo não tem jsdom).
// Uma função pura aqui é testável de verdade; a mesma lógica dentro do `.map()`
// de um componente React só seria conferível a olho.

import type { MnjOperacaoStatus, MnjOperacaoPrioridade } from "./schema.js";
import { diaCivilNaFazenda } from "./data-fazenda.js";

/** O mínimo que a ordenação precisa — nunca a linha inteira. */
export interface OperacaoOrdenavel {
  id: string;
  status: MnjOperacaoStatus;
  prioridade: MnjOperacaoPrioridade;
  /** `null` = backlog; backlog não aparece em Hoje. */
  dataPrevista: string | null;
  /** Fim do período planejado (migração 039). Manda no atraso quando existe. */
  dataFimPrevista?: string | null;
  /** Carimbo de `iniciada`. Uma operação iniciada ONTEM e não concluída está atrasada. */
  iniciadoEm?: Date | string | null;
}

/**
 * As três faixas da tela, na ordem em que aparecem. `fora` é tudo que a tela Hoje
 * não mostra: backlog, concluídas, canceladas e o que está planejado para outro dia.
 */
export type GrupoHoje = "atrasada" | "em_andamento" | "hoje" | "fora";

/** Prioridade → peso. `alta` primeiro; valor desconhecido cai como `normal`. */
const PESO_PRIORIDADE: Record<string, number> = { alta: 0, normal: 1, baixa: 2 };

const PESO_GRUPO: Record<GrupoHoje, number> = { atrasada: 0, em_andamento: 1, hoje: 2, fora: 3 };

/**
 * Em que faixa a operação cai, dado o dia civil da fazenda.
 *
 * 🔴 `hoje` tem que vir de `hojeNaFazenda()` (client) ou `dataHojeNaTz()` (server),
 * NUNCA de `new Date().toISOString()`. Em UTC-3, das 21:00 às 23:59, o
 * `toISOString` já devolve amanhã: a operação que vence hoje apareceria como
 * atrasada, e a de amanhã como de hoje. Esta função recebe a data pronta de
 * propósito — assim ela é testável com qualquer `TZ` e não tem como errar sozinha.
 *
 * Atraso (spec §5) = `planejada` com data anterior a hoje, **ou** `em_andamento`
 * iniciada em dia anterior. O segundo caso importa: uma operação que alguém
 * começou ontem e esqueceu de concluir não pode se esconder entre as de hoje.
 */
export function grupoDaOperacao(op: OperacaoOrdenavel, hoje: string): GrupoHoje {
  if (op.status === "concluida" || op.status === "cancelada" || op.status === "backlog") return "fora";
  if (!op.dataPrevista) return "fora";

  if (op.status === "em_andamento") {
    // Iniciada em dia anterior = atrasada, mesmo que a data prevista seja hoje.
    //
    // 🔴 `diaCivilNaFazenda`, nunca `toISOString()` cru. Uma fertirrigação
    // iniciada às 22:00 na Bahia é 01:00 UTC do DIA SEGUINTE: lendo em UTC, no
    // dia seguinte a comparação dava falso e a operação não era marcada
    // atrasada — ficava entre as de hoje, sem destaque, justamente a que mais
    // precisava dele. Fertirrigação noturna é rotina na Boa Vista.
    if (op.iniciadoEm && diaCivilNaFazenda(op.iniciadoEm) < hoje) return "atrasada";
    // Sem carimbo de início (dado antigo), cai na data prevista — o lado honesto.
    if (!op.iniciadoEm && (op.dataFimPrevista ?? op.dataPrevista) < hoje) return "atrasada";
    return "em_andamento";
  }

  // `planejada`: o atraso é medido pelo FIM do período quando ele existe — uma
  // operação de 10/08 a 15/08 não está atrasada no dia 11 (PR #134).
  const limite = op.dataFimPrevista ?? op.dataPrevista;
  if (limite < hoje) return "atrasada";
  if (op.dataPrevista <= hoje) return "hoje";
  return "fora";
}

/**
 * As operações de hoje, na ordem da tela. Quem está em `fora` sai da lista.
 *
 * Desempate dentro da faixa: prioridade (`alta` → `normal` → `baixa`) e depois a
 * data prevista — entre duas atrasadas de mesma prioridade, a mais velha primeiro.
 * `id` no fim para a ordem ser estável entre renders (duas operações idênticas não
 * podem trocar de lugar a cada refetch).
 */
export function ordenarParaHoje<T extends OperacaoOrdenavel>(
  operacoes: T[],
  hoje: string,
): Array<T & { grupo: Exclude<GrupoHoje, "fora"> }> {
  const visiveis: Array<T & { grupo: Exclude<GrupoHoje, "fora"> }> = [];
  for (const op of operacoes) {
    const grupo = grupoDaOperacao(op, hoje);
    if (grupo === "fora") continue;
    visiveis.push({ ...op, grupo });
  }
  visiveis.sort((a, b) => {
    const g = PESO_GRUPO[a.grupo] - PESO_GRUPO[b.grupo];
    if (g !== 0) return g;
    const p = (PESO_PRIORIDADE[a.prioridade] ?? 1) - (PESO_PRIORIDADE[b.prioridade] ?? 1);
    if (p !== 0) return p;
    const d = (a.dataPrevista ?? "").localeCompare(b.dataPrevista ?? "");
    if (d !== 0) return d;
    return a.id.localeCompare(b.id);
  });
  return visiveis;
}
